/// <reference path="../../../../node_modules/bingmaps/types/MicrosoftMaps/Microsoft.Maps.All.d.ts" />
import {EventEmitter, Injectable, OnDestroy} from '@angular/core';
import {environment} from "../../../environments/environment";
import {BingMapMarker} from "../bing-map-marker";
import {Library} from "../../search/object-detail/bottom-list/library-list/library.model";
import {AppConfigService} from "../../config/app-config.service";
import {MessageService} from "../../messages/message.service";
import {TranslateService} from "../../shared/translate.service";
import {ToastType} from "../../dialog/alert/toastr/toast-type.enum";
import {PushpinOptions} from "./pushpin.options";
import {HtmlUtilsService} from "../../html-utils/html-utils.service";
import {UserPositionService} from "./user-position.service";
import {BehaviorSubject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export abstract class AbstractMapService implements OnDestroy{

  protected mapInitialized = false;
  public accessDenied: boolean = false;
  protected pushpinColor: string = '#2578C2';
  protected pushpinIcon: string = 'assets/img/icons/location-number-small.png';

  mapPositionEmitter: EventEmitter<{ northwest: Microsoft.Maps.Location, southeast: Microsoft.Maps.Location }> = new EventEmitter();
  protected bingApiKey = environment.bingApiKey;

  protected directionsManager: any;
  protected directionsId: HTMLElement;

  protected infobox: any;
  protected routePath: any;
  protected userPin: any;
  protected map: any;
  protected destinationPin: BingMapMarker;
  protected showDistance = true;
  protected showRoute = true;

  protected north;
  protected south;
  protected west;
  protected east;

  selectedLib: Library;

  protected constructor(protected config: AppConfigService,
              protected messageService: MessageService,
              protected translateService: TranslateService,
              protected userPositionService: UserPositionService) {
  }

  get positionEmitter () : BehaviorSubject<any>{
    return this.userPositionService.positionEmitter;
  }

  public initMap(mapId: HTMLElement, directionsId: HTMLElement, showRoute?: boolean, showDistance?: boolean): Microsoft.Maps.Map {
    this.mapInitialized = false;
    this.accessDenied = false;
    this.userPin = undefined;
    this.userPositionService.position = undefined;
    this.directionsId = directionsId;
    this.destinationPin = undefined;

    if (this.userPositionService.getWatchId() != undefined) {
      navigator.geolocation.clearWatch(this.userPositionService.getWatchId());
      this.userPositionService.setWatchId(undefined);
    }
    if (showDistance) {
      this.showDistance = showDistance;
    }
    if (showRoute) {
      this.showRoute = showRoute;
    }

    //1. Create map
    this.map = this.createMapObject(mapId);

    Microsoft.Maps.loadModule(['Microsoft.Maps.Directions', 'Microsoft.Maps.SpatialMath'], () => {
      if (this.showRoute) {
        this.initPoints();
        this.customiseRouteTracing();
        this.addDirectionsHandler();

        if (this.directionsId) {
          this.directionsManager.setRenderOptions({itineraryContainer: this.directionsId});
        }
        if (!this.userPin) {
          this.userPin = new Microsoft.Maps.Pushpin(this.map.getCenter(), {visible: false});
          //Add a pushpin to show the user's location.
          this.map.entities.push(this.userPin);
        }
        this.resetMapLocation()
      }

      //4. Add infobox
      this.infobox = AbstractMapService.createInfoBox(this.map);
    });
    this.mapInitialized = true;
    return this.map;
  }

  public resetMapLocation(){
    //Watch the users location.
    if (!this.userPositionService.getWatchId()) {
      this.map.setView({
        bounds: Microsoft.Maps.LocationRect.fromLocations(this.north, this.south, this.west, this.east),
      });
      this.emitCurrentMapLocalization(this.map);
      this.userPositionService.setWatchId(navigator.geolocation.watchPosition(position => {
          this.centerOnUser(position);
          this.emitCurrentMapLocalization(this.map)
        }, error => {
          this.locationUnavailable(error);
          this.emitCurrentMapLocalization(this.map);
        }, {maximumAge: 0}
      ));
    }
    else {
      this.centerOnUser(this.userPositionService.position);
    }
  }

  protected locationUnavailable(error: PositionError) {
    if (!this.accessDenied) {
      console.error(error);
      this.userPositionService.positionEmitter.next(null);
      //create 4 points on the edge of the Poland and center on it!
      this.userPositionService.position = undefined;
      if (error.code === 1) {
        this.accessDenied = true;
      }
    }
    this.map.setView({
      bounds: Microsoft.Maps.LocationRect.fromLocations(this.north, this.south, this.west, this.east),
    });
    this.userPositionService.position = undefined;
    this.clearDirections();
  }

  public emitCurrentMapLocalization(map?: Microsoft.Maps.Map): void {
    let tempMap = map ? map : this.map;
    if (tempMap) {
      this.mapPositionEmitter.emit(AbstractMapService.getMapRectangleCoords(map));
    }
  }

  public clearPushpins() {
    if (this.map) {
      for (let i = this.map.entities.getLength() - 1; i >= 0; i--) {
        let pushpin = this.map.entities.get(i);
        if (pushpin instanceof Microsoft.Maps.Pushpin) {
          if (!this.userPositionService.position ||
            (this.userPositionService.position.coords.latitude !== pushpin.getLocation().latitude
              || this.userPositionService.position.coords.longitude !== pushpin.getLocation().longitude)
          )
            this.map.entities.removeAt(i);
        }
      }
    }
  }

  /**
   * Create a bing map inside selected div with pushpins and infobox.
   * @param {HTMLElement} mapId div id
   * @param {HTMLElement} directionsId directions div id (needed if you want to show directions panel)
   * @param {BingMapMarker[]} markers pushpins
   * @param {boolean} showRoute should route between points be visible
   * @param {boolean} showDistance does distance info should be visible on pushpin?
   * @returns {Microsoft.Maps.Map} Microsoft Bing map object
   */
  public generateMap(mapId: HTMLElement, directionsId: HTMLElement, markers: BingMapMarker[], showRoute?: boolean, showDistance?: boolean): Microsoft.Maps.Map {

    Microsoft.Maps.loadModule(['Microsoft.Maps.Directions'], () => {
      this.initMap(mapId, directionsId, showRoute, showDistance);
      this.addPushpins(markers);
    });
    return this.map;
  }

  protected initPoints(): any {
    this.north = new Microsoft.Maps.Location(54.83226, 18.0746);
    this.south = new Microsoft.Maps.Location(49.000284, 22.871096);
    this.west = new Microsoft.Maps.Location(52.857893, 14.150698);
    this.east = new Microsoft.Maps.Location(53.152079, 23.934023);
  }

  protected customiseRouteTracing() {
    this.directionsManager = new Microsoft.Maps.Directions.DirectionsManager(this.map);
    this.directionsManager.setRequestOptions({
      distanceUnit: Microsoft.Maps.Directions.DistanceUnit.km
    });
    this.directionsManager.setRenderOptions({
      drivingPolylineOptions: {
        strokeColor: this.pushpinColor,
        strokeThickness: 6
      }
    });
  }

  protected stopTracking() {
    //Remove the user pushpin.
    this.map.entities.clear();
    this.clearDirections();
  }

  protected createMapObject(mapId: HTMLElement): Microsoft.Maps.Map {
    let map = new Microsoft.Maps.Map(mapId, {
        credentials: this.bingApiKey,
        enableCORS: true,
        enableHighDpi: true,
        showMapTypeSelector: false,
        enableClickableLogo: false,
        customMapStyle: {
          'version': '1.0',
          elements: {
            road: { strokeColor: 'black'},
            forest: {visible: false},
            park: {visible: false}
          }
        }
      });

    Microsoft.Maps.Events.addHandler(map, 'viewchangeend', () => this.emitCurrentMapLocalization(map));
    Microsoft.Maps.Events.addHandler(map, 'mousewheel', (event) => this.checkIfScrollIsPossible(map));
    return map;
  }

  public checkIfScrollIsPossible(map?: Microsoft.Maps.Map) {
    if (!(<KeyboardEvent>window.event).ctrlKey) {
      map.setOptions({ disableScrollWheelZoom: true });
    } else {
      map.setOptions({ disableScrollWheelZoom: false });
    }

  }

  public clearDirections() {
    //Clear directions waypoints and display without clearing it's options.
    if(this.directionsManager) {
      this.directionsManager.clearDisplay();
      const wp = this.directionsManager.getAllWaypoints();
      if (wp && wp.length > 0) {
        for (let i = wp.length - 1; i >= 0; i--) {
          this.directionsManager.removeWaypoint(wp[i]);
        }
      }
      this.directionsManager.calculateDirections();
    }
    this.routePath = null;
  }

  /**
   * Draws route between user and destination when map is initialized & showRoute = true
   * @param {BingMapMarker} dest
   * @param {string} name
   */
  public getRouteToTarget(dest: BingMapMarker, name?: string): void {
    if (this.accessDenied) {
      let entities = this.map.entities;
      entities._primitives.forEach((val) => {
        if (val.metadata.title === name) {
          Microsoft.Maps.Events.invoke(val, 'click', {target: val})
        }
      });
    } else {
      if (this.directionsManager) {
        if (this.userPositionService.position && dest && !this.accessDenied) {
          this.destinationPin = dest;
          this.drawRoute(new Microsoft.Maps.Pushpin(new Microsoft.Maps.Location(this.destinationPin.lat, this.destinationPin.lon), {color: this.pushpinColor}));
        } else {
          this.messageService.add({
            message: 'Nie można wyznaczyć trasy. Lokalizacja nie została jeszcze określona.',
            title: 'Uwaga',
            type: ToastType.WARNING
          });
        }
      }
    }
  }

  protected drawRoute(destinP: Microsoft.Maps.Pushpin) {
    if (this.userPositionService.position && !this.accessDenied) {
      this.clearDirections();
      //Create waypoints to route between.
      this.directionsManager.addWaypoint(new Microsoft.Maps.Directions.Waypoint({
        location: new Microsoft.Maps.Location(
          this.userPositionService.position.coords.latitude,
          this.userPositionService.position.coords.longitude
        )
      }));
      this.directionsManager.addWaypoint(new Microsoft.Maps.Directions.Waypoint({
        location: destinP.getLocation()
      }));
      //Calculate directions.
      this.directionsManager.calculateDirections();
    }
    else {
      this.handlePermission();
    }
    this.emitCurrentMapLocalization();
  }

  protected static createInfoBox(map: Microsoft.Maps.Map): Microsoft.Maps.Infobox {
    //Create an infobox at the center of the map but don't show it.
    const infobox = new Microsoft.Maps.Infobox(map.getCenter(), {
      visible: false
    });
    //Assign the infobox to a map instance.
    infobox.setMap(map);
    return infobox;
  }

  protected centerOnUser(position): any {
    if (position && position.coords && !this.accessDenied) {
      let loc = new Microsoft.Maps.Location(position.coords.latitude,
        position.coords.longitude);
      if (!this.userPositionService.position || this.isPositionOutdated(new Microsoft.Maps.Location(this.userPositionService.position.coords.latitude,
        this.userPositionService.position.coords.longitude), loc, 100)) {
        //Update the user pushpin.
        this.userPin.setLocation(loc);
        this.accessDenied = false;
        this.userPin.setOptions({visible: true, color: this.pushpinColor});
        //Center the map on the user's location.
        if (!this.userPositionService.position) {
          this.map.entities.push(this.userPin);
          this.map.setView({center: this.userPin.getLocation(), zoom: this.config.zoomConfig});
        } else {
          this.map.entities.push(this.userPin);
        }
        // update list of libraries if user move at least 1 km
        if (this.isPositionOutdated(this.userPositionService.lastPosition, loc, 1000)) {
          this.userPositionService.lastPosition = new BingMapMarker(loc.latitude, loc.longitude);
          this.userPositionService.positionEmitter.next(new BingMapMarker(loc.latitude, loc.longitude, undefined));
        }
        this.userPositionService.position = position;
        this.updateRoute();
      }
    } else {
      if (this.userPin) {
        this.userPin.setOptions({visible: false, color: this.pushpinColor});
        this.map.setView({
          bounds: Microsoft.Maps.LocationRect.fromLocations(this.north, this.south, this.west, this.east),
        });
      }
    }
  }

  protected isPositionOutdated(loc1, loc2: Microsoft.Maps.Location, distance: number): boolean {
    return !this.userPositionService.position || Microsoft.Maps.SpatialMath.getDistanceTo(loc1, loc2, Microsoft.Maps.SpatialMath.DistanceUnits.Meters) > distance;
  }

  protected updateRoute() {
    if (!this.accessDenied && this.userPositionService.position && this.destinationPin) {
      this.getRouteToTarget(this.destinationPin);
    }
  }

  ngOnDestroy(): void {
    this.stopTracking();
  }

  addPushpins(markers: BingMapMarker[]) {
    const pushpins: Microsoft.Maps.Pushpin[] = [];

    for (const marker of markers) {
      const loc = new Microsoft.Maps.Location(marker.lat, marker.lon);
      const options = new PushpinOptions();
      if (marker.label) {
        options.text = marker.label;
      }
      options.color = this.pushpinColor;
      options.icon = this.pushpinIcon;
      options.roundClickableArea = true;
      const pushpin = new Microsoft.Maps.Pushpin(loc, options);

      pushpin.metadata = {};

      if (marker.title) {
        pushpin.metadata.title = marker.title;
      }
      if (marker.description) {
        pushpin.metadata.description = marker.description;
      }
      //Add a click event handler to the pushpin.
      Microsoft.Maps.Events.addHandler(pushpin, 'click', (e) => {
        //Make sure the infobox has metadata to display.
        if (e['target'].metadata) {
          let desc = e['target'].metadata.description;
          if (this.showDistance && this.userPin && !this.accessDenied) {
            desc += '<span style="display: table-row">' +
              '<b>' +
              this.userPositionService.createDistanceLabel(
                new BingMapMarker(e['target'].getLocation().latitude, e['target'].getLocation().longitude),
                false) +
              '</b></span>';
            desc = AbstractMapService.addButton(desc, marker);
          } else if (this.accessDenied) {
            desc += `<span>${this.translateService.translate('bing-maps.no.location')}</span>`;
            desc = AbstractMapService.addButton(desc, marker);
          }
          //Set the infobox options with the metadata of the pushpin.
          this.infobox.setOptions({
            location: e['target'].getLocation(),
            title: e['target'].metadata.title,
            description: desc,
            visible: true
          });
        }
      });
      pushpins.push(pushpin);
    }
    this.map.entities.push(pushpins);
  }

  protected static getMapRectangleCoords(map: Microsoft.Maps.Map): { northwest: Microsoft.Maps.Location, southeast: Microsoft.Maps.Location } | undefined {
    if (map) {
      if (map.getBounds()) {
        return {
          northwest: map.getBounds().getNorthwest(),
          southeast: map.getBounds().getSoutheast()
        };
      }
    }
    return undefined;
  }

  public static isLibraryVisible(library: Library, lat1: number, lat2: number, lon1: number, lon2: number) {
    if (library) {
      if (library.online) {
        return true;
      } else {
        const minLat = Math.min(lat1, lat2);
        const maxLat = Math.max(lat1, lat2);
        const minLon = Math.min(lon1, lon2);
        const maxLon = Math.max(lon1, lon2);
        return minLat <= library.lat && library.lat <= maxLat &&
          minLon <= library.lon && library.lon <= maxLon;
      }
    }
  }

  protected static addButton(desc, marker) {
    let url = HtmlUtilsService.createUrl(marker.url);

    if (url && url !== undefined) {
      desc += '<span>' +
        '<div style="margin-top: 14px; margin-bottom: 4px;"><a href="' +
        url +
        '" class="map-pin-push"' +
        '>Przejdź</a></div>' +
        '</span>';
    }
    return desc;
  }

  protected addDirectionsHandler() {
    Microsoft.Maps.Events.addHandler(this.directionsManager, 'directionsUpdated', () => {
      const route = this.directionsManager.getCurrentRoute();
      if (route && route.routePath && route.routePath.length > 0) {
        this.routePath = new Microsoft.Maps.Polyline(route.routePath);
      }
    });
  }


  protected handlePermission() {
    if (this.accessDenied) {
      navigator['permissions'].query({name: 'geolocation'}).then(function (result) {
        if (result.state == 'granted') {
          AbstractMapService.report(result.state);
        } else if (result.state == 'prompt') {
          AbstractMapService.report(result.state);
        } else if (result.state == 'denied') {
          AbstractMapService.report(result.state);
        }
        result.onchange = function () {
          AbstractMapService.report(result.state);
        }
      });
    }
  }

  protected static report(state) {
    console.log('Permission ' + state);
  }
}
